import { ArrowRight, MessageSquare } from 'lucide-react';

import Container from '../../Main/sections/Container';
import Reveal from '../../../components/Reveal';

const REGISTER_URL = 'https://app.schoolhack.ai/auth/register';

const Avatar = ({ label, color }) => (
  <span
    className="flex h-6 w-6 items-center justify-center rounded-full text-[0.6rem] font-bold text-[#04110c] ring-2 ring-[#0b100e]"
    style={{ background: color }}
  >
    {label}
  </span>
);

const Cursor = ({ name, color, style }) => (
  <span className="pointer-events-none absolute z-10 flex flex-col items-start" style={style}>
    <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
      <path d="M1 1L1 13L4.5 9.8L7 15L9 14L6.5 8.8L11 8.5Z" fill={color} stroke="#0b100e" strokeWidth="0.8" />
    </svg>
    <span
      className="mt-0.5 rounded px-1.5 py-0.5 text-[0.6rem] font-semibold text-[#04110c]"
      style={{ background: color }}
    >
      {name}
    </span>
  </span>
);

const Bar = ({ w, c = 'bg-white/12' }) => (
  <span className={`block h-2.5 rounded-full ${c}`} style={{ width: w }} />
);

const CollabWorkspace = () => (
  <div className="card-dark overflow-hidden rounded-2xl">
    {/* top bar */}
    <div className="flex items-center gap-2 border-b border-white/8 px-4 py-3">
      <span className="flex gap-1.5">
        <span className="h-2 w-2 rounded-full bg-white/15" />
        <span className="h-2 w-2 rounded-full bg-white/15" />
        <span className="h-2 w-2 rounded-full bg-white/15" />
      </span>
      <span className="ml-1 text-[0.72rem] font-semibold text-white">Cell Biology — Shared Notes</span>
      <div className="ml-auto flex items-center gap-2">
        <div className="flex -space-x-2">
          <Avatar label="A" color="#5ED8B2" />
          <Avatar label="M" color="#60a5fa" />
          <Avatar label="S" color="#f472b6" />
        </div>
        <span className="flex items-center gap-1 rounded-full bg-accent/15 px-2 py-1 text-[0.6rem] font-semibold text-accent">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
          Live
        </span>
      </div>
    </div>

    {/* doc body */}
    <div className="relative p-5">
      <h4 className="mb-4 text-sm font-semibold text-white">Phases of Mitosis</h4>
      <div className="flex flex-col gap-3">
        <Bar w="94%" />
        <Bar w="82%" />
        {/* highlighted (someone's selection) */}
        <span className="block h-2.5 w-[70%] rounded-full bg-accent/35 ring-1 ring-accent/50" />
        <Bar w="88%" />
        <Bar w="60%" />
        <Bar w="78%" />
        <Bar w="45%" />
      </div>

      {/* live cursors */}
      <Cursor name="Maya" color="#60a5fa" style={{ top: '46%', left: '62%' }} />
      <Cursor name="Sam" color="#f472b6" style={{ top: '72%', left: '30%' }} />

      {/* margin comment */}
      <div className="mt-5 flex items-start gap-2 rounded-xl border border-white/8 bg-white/[0.03] p-3">
        <Avatar label="M" color="#60a5fa" />
        <div>
          <span className="text-[0.68rem] font-semibold text-white/70">Maya</span>
          <p className="text-[0.72rem] leading-relaxed text-white/60">
            Should we add a diagram for anaphase here? 💡
          </p>
        </div>
      </div>
    </div>
  </div>
);

const Hero = () => {
  return (
    <section className="relative overflow-hidden pb-16 pt-14 md:pt-48 lg:pb-24">
      <div className="glow left-1/2 top-[-140px] h-[420px] w-[620px] -translate-x-1/2 bg-accent/16" />
      <div className="glow right-[-120px] top-[120px] h-[360px] w-[360px] bg-accent/10" />
      <div className="pointer-events-none absolute inset-0 grid-overlay opacity-60" />

      <Container className="relative grid items-center gap-12 lg:grid-cols-[1.05fr_1fr]">
        {/* copy */}
        <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
          <Reveal>
            <span className="eyebrow">Collaborative Mode</span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-heading mt-6 text-[2.3rem] font-bold leading-[1.08] sm:text-[3rem] lg:text-[3.5rem]">
              <span className="head-fade">Study Together.</span>
              <br />
              <span className="text-accent-grad">Think Faster.</span>
              <br />
              <span className="head-fade">Score Higher.</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mx-auto mt-6 w-full max-w-[32rem] text-[0.98rem] leading-relaxed text-white/60 lg:mx-0">
              Collaborative Mode turns any School Hack note into a shared, real-time
              workspace. Edit together, highlight and comment, run group quizzes, and
              watch your classmates&apos; cursors move across the page — all live, all
              free.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <a
              href={REGISTER_URL}
              target="_blank"
              rel="noreferrer"
              className="btn-primary mt-8 px-7 py-3.5 text-[0.95rem]"
            >
              Start a study room
              <ArrowRight size={17} />
            </a>
          </Reveal>
        </div>

        {/* workspace mockup */}
        <Reveal delay={200} className="relative mx-auto w-full max-w-[440px]">
          <div className="glow left-1/2 top-1/2 h-[320px] w-[360px] -translate-x-1/2 -translate-y-1/2 bg-accent/14" />
          <div className="relative animate-float-slow">
            <CollabWorkspace />
          </div>
          {/* floating chat chip */}
          <div className="glass absolute -bottom-4 -left-4 hidden items-center gap-2 rounded-xl px-3 py-2 sm:flex">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent text-[#04110c]">
              <MessageSquare size={14} />
            </span>
            <div className="text-left">
              <div className="text-[0.72rem] font-semibold text-white">3 studying now</div>
              <div className="text-[0.6rem] text-white/45">in this room</div>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
};

export default Hero;
